import { apiRequest, resolveApiUrl } from './client';

// --- Types ---

export type PhotoControlScheduleItem = {
  readonly id: string;
  readonly treatment_id: string;
  readonly interval_days: number;
  readonly start_date: string;
  readonly end_date: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly required_angles: readonly string[] | null;
  readonly requires_video: boolean;
};

// Canonical photo angle keys in order, with Ukrainian labels.
export const PHOTO_ANGLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'front_closed', label: 'Центр, рот закритий' },
  { value: 'left', label: 'Зліва' },
  { value: 'right', label: 'Справа' },
  { value: 'front_open', label: 'Центр, рот відкритий' },
  { value: 'lower_open', label: 'Знизу, рот відкритий' },
  { value: 'selfie_smile', label: 'Селфі з посмішкою' },
  { value: 'smile_zoom', label: 'Посмішка наближено' },
  { value: 'aligners_zoom', label: 'З елайнерами, наближено' },
];

export type PhotoControlImageItem = {
  readonly id: string;
  readonly content_url: string;
  readonly angle_type: string;
  readonly byte_size: number;
  readonly mime_type: string;
  readonly sort_order: number;
};

export type PhotoControlEntryListItem = {
  readonly id: string;
  readonly due_date: string;
  readonly status: 'pending' | 'submitted' | 'reviewed';
  readonly image_count: number;
  readonly doctor_comment_preview: string | null;
  readonly cycle_number: number | null;
  readonly stage_title: string | null;
  readonly latest_image_url: string | null;
  readonly trigger_source: 'scheduled' | 'wearing_stage_entry';
};

export type PhotoControlEntryDetail = {
  readonly id: string;
  readonly due_date: string;
  readonly status: 'pending' | 'submitted' | 'reviewed';
  readonly images: readonly PhotoControlImageItem[];
  readonly doctor_comment: string | null;
  readonly doctor_comment_at: string | null;
  readonly doctor_display_name: string | null;
  readonly doctor_avatar_url: string | null;
  readonly cycle_number: number | null;
  readonly stage_kind: string | null;
  readonly stage_title: string | null;
  readonly video_url: string | null;
  readonly video_submitted: boolean;
};

export type CreatePhotoControlSchedulePayload = {
  readonly treatment_id: string;
  readonly interval_days: number;
  readonly start_date: string;
  readonly end_date?: string | null;
  readonly required_angles?: readonly string[] | null;
  readonly requires_video?: boolean;
};

export type UpdatePhotoControlSchedulePayload = {
  readonly interval_days?: number;
  readonly end_date?: string | null;
  readonly is_active?: boolean;
  readonly required_angles?: readonly string[] | null;
  readonly requires_video?: boolean;
};

export type UpdatePhotoControlEntryPayload = {
  readonly doctor_comment?: string | null;
  readonly status?: 'pending' | 'submitted' | 'reviewed';
};

// --- Schedules ---

export function getPhotoControlSchedules(
  patientId: string,
  treatmentId: string,
  accessToken: string,
): Promise<readonly PhotoControlScheduleItem[]> {
  return apiRequest<readonly PhotoControlScheduleItem[]>(
    `/admin/patients/${patientId}/photo-control-schedules?treatment_id=${encodeURIComponent(treatmentId)}`,
    { method: 'GET', token: accessToken },
  );
}

export function createPhotoControlSchedule(
  patientId: string,
  payload: CreatePhotoControlSchedulePayload,
  accessToken: string,
): Promise<PhotoControlScheduleItem> {
  return apiRequest<PhotoControlScheduleItem>(
    `/admin/patients/${patientId}/photo-control-schedules`,
    { method: 'POST', body: payload, token: accessToken },
  );
}

export function updatePhotoControlSchedule(
  patientId: string,
  scheduleId: string,
  payload: UpdatePhotoControlSchedulePayload,
  accessToken: string,
): Promise<PhotoControlScheduleItem> {
  return apiRequest<PhotoControlScheduleItem>(
    `/admin/patients/${patientId}/photo-control-schedules/${scheduleId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}

export function deletePhotoControlSchedule(
  patientId: string,
  scheduleId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/photo-control-schedules/${scheduleId}`,
    { method: 'DELETE', token: accessToken },
  );
}

// --- Entries ---

export function getPhotoControlEntries(
  patientId: string,
  accessToken: string,
  params?: { status?: string; limit?: number; offset?: number },
): Promise<readonly PhotoControlEntryListItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('entry_status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const qs = searchParams.toString();
  const path = `/admin/patients/${patientId}/photo-controls${qs ? `?${qs}` : ''}`;
  return apiRequest<readonly PhotoControlEntryListItem[]>(path, {
    method: 'GET',
    token: accessToken,
  });
}

export function updatePhotoControlEntry(
  patientId: string,
  entryId: string,
  payload: UpdatePhotoControlEntryPayload,
  accessToken: string,
): Promise<PhotoControlEntryDetail> {
  return apiRequest<PhotoControlEntryDetail>(
    `/admin/patients/${patientId}/photo-controls/${entryId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}

export function getPhotoControlEntryDetail(
  patientId: string,
  entryId: string,
  accessToken: string,
): Promise<PhotoControlEntryDetail> {
  return apiRequest<PhotoControlEntryDetail>(
    `/admin/patients/${patientId}/photo-controls/${entryId}`,
    { method: 'GET', token: accessToken },
  );
}

export function getPhotoControlImageUrl(
  patientId: string,
  entryId: string,
  imageId: string,
): string {
  return resolveApiUrl(
    `/admin/patients/${patientId}/photo-controls/${entryId}/photos/${imageId}/content`,
  );
}

export function getPhotoControlVideoUrl(patientId: string, entryId: string): string {
  return resolveApiUrl(
    `/admin/patients/${patientId}/photo-controls/${entryId}/video/content`,
  );
}
